import type { MapIssue, ObjectRef } from '../../sim/map/index.ts';

export type EditorLogFilter = 'both' | 'errors' | 'warnings';

export type EditorLogBadge = 'error' | 'warning';

export type EditorLogEntry = {
  id: string;
  issueId: string;
  severity: 'error' | 'warning';
  message: string;
  ref: ObjectRef | null;
  at: number;
  /** True once the fault is gone. The line stays in the history, grayed in the pane. */
  resolved: boolean;
};

/** How many lines the pinned pane keeps; older history lives behind Expand log. */
export const EDITOR_LOG_PREVIEW = 3;

/**
 * Appends a line for each validation issue that was not already live. Cleared faults drop out of
 * `seen` so they can log again if they come back; those older lines stay and are marked resolved.
 */
export function appendValidationLog(
  entries: readonly EditorLogEntry[],
  seen: Set<string>,
  issues: readonly MapIssue[],
  now = Date.now(),
): { entries: EditorLogEntry[]; added: number } {
  const live = new Set(issues.map((issue) => issue.id));
  let next = [...entries];
  let resolvedChanged = false;

  for (const id of [...seen]) {
    if (live.has(id)) {
      continue;
    }

    seen.delete(id);
    next = next.map((entry) => {
      if (entry.issueId !== id || entry.resolved) {
        return entry;
      }

      resolvedChanged = true;
      return { ...entry, resolved: true };
    });
  }

  const added: EditorLogEntry[] = [];

  for (const issue of issues) {
    if (seen.has(issue.id)) {
      continue;
    }

    seen.add(issue.id);
    added.push({
      id: `${issue.id}#${next.length + added.length}`,
      issueId: issue.id,
      severity: issue.severity,
      message: issue.message,
      ref: issue.ref,
      at: now,
      resolved: false,
    });
  }

  return {
    entries: added.length > 0 ? [...next, ...added] : resolvedChanged ? next : [...entries],
    added: added.length,
  };
}

export function previewLogEntries(
  entries: readonly EditorLogEntry[],
  limit = EDITOR_LOG_PREVIEW,
): EditorLogEntry[] {
  return entries.length <= limit ? [...entries] : entries.slice(-limit);
}

export function filterLogEntries(
  entries: readonly EditorLogEntry[],
  filter: EditorLogFilter,
): EditorLogEntry[] {
  if (filter === 'errors') {
    return entries.filter((entry) => entry.severity === 'error');
  }

  if (filter === 'warnings') {
    return entries.filter((entry) => entry.severity === 'warning');
  }

  return [...entries];
}
